/**
 * Superadmin — Doctor Management
 * Liste tous les médecins, permet de gérer spécialité, INPE, cabinet, statut, cotation.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { User, Search, Plus, Edit, Trash2, Save, Stethoscope, Building2, MapPin, Hash, Settings, Award, Activity } from "lucide-react";
import { MOCK_DOCTORS, SPECIALTY_LABEL, type DoctorCotation } from "@/lib/referential/doctor-cotations";
import { MOCK_COTATIONS } from "@/lib/referential/doctor-cotations";

export const Route = createFileRoute("/admin/doctors")({
  component: DoctorsAdmin,
});

function DoctorsAdmin() {
  const [search, setSearch] = useState("");
  const [filterSpecialty, setFilterSpecialty] = useState<string>("all");
  const [editing, setEditing] = useState<typeof MOCK_DOCTORS[0] | null>(null);
  const [adding, setAdding] = useState(false);

  const specialties = Array.from(new Set(MOCK_DOCTORS.map((d) => d.specialty)));

  const filtered = MOCK_DOCTORS.filter((d) => {
    if (filterSpecialty !== "all" && d.specialty !== filterSpecialty) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return d.name.toLowerCase().includes(s) || d.inpe.includes(s) || d.rpps.includes(s) || d.cabinet.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2">
        <Badge variant="destructive" className="text-[10px]">SUPERADMIN</Badge>
        <span className="text-sm text-muted-foreground">· Gestion des médecins</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Stethoscope className="h-5 w-5" /> Gestion des médecins
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {MOCK_DOCTORS.length} praticiens · Spécialité, INPE, cabinet, cotation
        </p>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Rechercher (nom, INPE, RPPS, cabinet)..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={filterSpecialty} onValueChange={setFilterSpecialty}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Toutes spécialités" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            {specialties.map((s) => <SelectItem key={s} value={s}>{SPECIALTY_LABEL[s] ?? s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setAdding(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Nouveau médecin</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-muted-foreground">
                <th className="text-left p-3 text-xs uppercase">Médecin</th>
                <th className="text-left p-3 text-xs uppercase">Spécialité</th>
                <th className="text-left p-3 text-xs uppercase">INPE / RPPS</th>
                <th className="text-left p-3 text-xs uppercase">Cabinet</th>
                <th className="text-left p-3 text-xs uppercase">Ville</th>
                <th className="text-left p-3 text-xs uppercase">Cotation</th>
                <th className="text-right p-3 text-xs uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const cot = MOCK_COTATIONS.find((c) => c.doctorId === d.id);
                return (
                  <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-3">
                      <div className="font-medium">{d.name}</div>
                      <div className="text-[10px] text-muted-foreground font-mono">{d.id}</div>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline">{SPECIALTY_LABEL[d.specialty] ?? d.specialty}</Badge>
                    </td>
                    <td className="p-3 font-mono text-xs">
                      <div>{d.inpe}</div>
                      <div className="text-muted-foreground">{d.rpps}</div>
                    </td>
                    <td className="p-3 text-xs">{d.cabinet}</td>
                    <td className="p-3 text-xs">{d.city}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        <Badge variant="secondary" className="text-[10px]">{cot?.defaultAllowedActs.length} actes</Badge>
                        {cot?.customAssignments.length ? <Badge variant="outline" className="text-[10px]">+{cot.customAssignments.length}</Badge> : null}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" asChild>
                          <a href="/admin/doctor-cotations"><Settings className="h-3.5 w-3.5" /></a>
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(d)}><Edit className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost"><Activity className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="ghost"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Edit / Add doctor dialog */}
      <Dialog open={!!editing || adding} onOpenChange={(o) => { if (!o) { setEditing(null); setAdding(false); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-4 w-4" /> {adding ? "Nouveau médecin" : "Modifier le médecin"}
            </DialogTitle>
            <DialogDescription>Toutes les modifications sont journalisées dans l'audit WORM</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom complet" value={editing?.name ?? ""} />
            <Field label="Spécialité" value={SPECIALTY_LABEL[editing?.specialty ?? "MED"] ?? ""} />
            <Field label="INPE" value={editing?.inpe ?? ""} />
            <Field label="RPPS" value={editing?.rpps ?? ""} />
            <Field label="Cabinet" value={editing?.cabinet ?? ""} />
            <Field label="Ville" value={editing?.city ?? ""} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setAdding(false); }}>Annuler</Button>
            <Button><Save className="h-3.5 w-3.5 mr-1" /> Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground uppercase">{label}</label>
      <Input defaultValue={value} className="mt-1" />
    </div>
  );
}
